import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import type { StartProcessingRequest, StartProcessingResponse, BalanceCheckResult } from '../api/scan-types'
import type { WeighbridgeTicket, FirstWeighRequest, SecondWeighRequest, MarkDeliveredRequest } from '../api/weighbridge-types'

export interface WeighbridgeState {
  firstWeighTicket: WeighbridgeTicket | null
  secondWeighTicket: WeighbridgeTicket | null
  loading: boolean
  error: string | null
  firstWeighSuccess: boolean
  secondWeighSuccess: boolean
  deliveredSuccess: boolean
  startProcessingResult: StartProcessingResponse | null
  startProcessingSuccess: boolean
  /** Balance check result from last failed balance check */
  balanceCheckError: BalanceCheckResult | null
}

/** Payload for failures that may include balance check details */
export interface BalanceCheckFailurePayload {
  message: string
  balanceCheck?: BalanceCheckResult
}

const initialState: WeighbridgeState = {
  firstWeighTicket: null,
  secondWeighTicket: null,
  loading: false,
  error: null,
  firstWeighSuccess: false,
  secondWeighSuccess: false,
  deliveredSuccess: false,
  startProcessingResult: null,
  startProcessingSuccess: false,
  balanceCheckError: null,
}

export const weighbridgeSlice = createSlice({
  name: 'shipments/weighbridge',
  initialState,
  reducers: {
    // First Weigh
    firstWeigh: (state, _action: PayloadAction<FirstWeighRequest>) => {
      state.loading = true
      state.error = null
      state.firstWeighSuccess = false
      state.firstWeighTicket = null
    },
    firstWeighSuccess: (state, action: PayloadAction<WeighbridgeTicket>) => {
      state.loading = false
      state.firstWeighSuccess = true
      state.firstWeighTicket = action.payload
      state.error = null
    },
    firstWeighFailure: (state, action: PayloadAction<string>) => {
      state.loading = false
      state.error = action.payload
      state.firstWeighSuccess = false
    },

    // Second Weigh
    secondWeigh: (state, _action: PayloadAction<SecondWeighRequest>) => {
      state.loading = true
      state.error = null
      state.secondWeighSuccess = false
      state.secondWeighTicket = null
    },
    secondWeighSuccess: (state, action: PayloadAction<WeighbridgeTicket>) => {
      state.loading = false
      state.secondWeighSuccess = true
      state.secondWeighTicket = action.payload
      state.error = null
    },
    secondWeighFailure: (state, action: PayloadAction<string>) => {
      state.loading = false
      state.error = action.payload
      state.secondWeighSuccess = false
    },

    // Mark Delivered
    markDelivered: (state, _action: PayloadAction<MarkDeliveredRequest>) => {
      state.loading = true
      state.error = null
      state.deliveredSuccess = false
    },
    markDeliveredSuccess: (state) => {
      state.loading = false
      state.deliveredSuccess = true
      state.error = null
    },
    markDeliveredFailure: (state, action: PayloadAction<string>) => {
      state.loading = false
      state.error = action.payload
      state.deliveredSuccess = false
    },

    // Start Processing
    startProcessing: (state, _action: PayloadAction<StartProcessingRequest>) => {
      state.loading = true
      state.error = null
      state.startProcessingSuccess = false
      state.startProcessingResult = null
      state.balanceCheckError = null
    },
    startProcessingSuccess: (state, action: PayloadAction<StartProcessingResponse>) => {
      state.loading = false
      state.startProcessingSuccess = true
      state.startProcessingResult = action.payload
      state.error = null
      state.balanceCheckError = null
    },
    startProcessingFailure: (state, action: PayloadAction<string | BalanceCheckFailurePayload>) => {
      state.loading = false
      state.startProcessingSuccess = false
      // Handle both string and object payloads
      if (typeof action.payload === 'string') {
        state.error = action.payload
        state.balanceCheckError = null
      } else {
        state.error = action.payload.message
        state.balanceCheckError = action.payload.balanceCheck ?? null
      }
    },

    // Resets
    resetFirstWeigh: (state) => {
      state.firstWeighTicket = null
      state.firstWeighSuccess = false
      state.error = null
    },
    resetSecondWeigh: (state) => {
      state.secondWeighTicket = null
      state.secondWeighSuccess = false
      state.error = null
    },
    resetDelivered: (state) => {
      state.deliveredSuccess = false
      state.error = null
    },
    resetStartProcessing: (state) => {
      state.startProcessingResult = null
      state.startProcessingSuccess = false
      state.error = null
      state.balanceCheckError = null
    },
    resetAll: () => {
      return initialState
    }
  },
})

export const weighbridgeActions = weighbridgeSlice.actions
export const weighbridgeReducer = weighbridgeSlice.reducer
